import { OmniElement, OmniStyle, OmniIcon, html, css } from 'omni-ui';
import { formComponent } from './form-component.js';
import { Om2OptionsList, noblank } from './om2-options-list.js';
import { sleep } from './om2-util.js';

OmniStyle.register();
OmniIcon.register();
Om2OptionsList.register();

/* Features
 * - Single or multiple select dropdown.
 * - Support saving/reading selected options as objects or values.
 * - Search and filter available options
 * - Custom async search (for things like fetching from backend)
 * - Keyboard navigation
 * - Built on omni-ui
 * - Styled to look like other bulma/omniui elements
 * - Disabled options supported in dropdown (disabled: true)
 * - Emits change event: you can listen for event and check the value property
 * - Configure the object properties to use as an id (unique) and as a display label
 *
 * <om2-dropdown
 *   ismulti
 *   hassearch
 *   .options=${[{label: 'one', value: 1}, {label: 'two', value: 2}, {label: 'three', value: 3}]}
 *   .value=${[1, 2]}
 *   @change=${(e) => console.log(`Value changed to: ${e.detail.value}`)}
 *   placeholder="Numbers"></om2-dropdown>
 */

export class Om2Dropdown extends Om2OptionsList {
  constructor() {
    super();
    this.hasSearch = false;
    this.allowClear = false;
    this.borderless = false;

    this.isActive = false;
    this.filteredOptions = [];
  }

  static get properties() {
    return {
      /* Add a search box in the dropdown to filter visible choices */
      hasSearch: { type: Boolean },

      /* Provide an async custom search function. Takes a search string and
       * returns a list of matching options. Can be used for fetch/ajax etc.
       */
      asyncSearch: { type: Function },

      /* Allow deselecting/clearing values. (dropdown value will be undefined)
       */
      allowClear: { type: Boolean },

      /* Placeholder text to display */
      placeholder: { type: String },

      /* Display the dropdown trigger without borders */
      borderless: { type: Boolean },

      /* Dropdown grows to fit content ignoring the width of the trigger */
      autoWidth: { type: Boolean },

      /* Customize how the selected items in the trigger area are rendered.
       * .renderSelected=${(dropdown, selected) => html`Count: (${selected.length})`}
       */
      renderSelected: { attribute: false },

      /* Properties from parent class (Om2OptionsList):
       *
       * hasObjectValues
       * isMulti
       * options
       * optionsTree
       * value
       * valueLabel
       * idProperty
       * nameProperty
       * childrenProperty
       * sort
       */

      /* Below are for internal use */
      isActive: { state: true },
      search: { state: true },
      loading: { state: true },
      filteredOptions: { state: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        /* The dropdown content scrolls beyond max height and is above other
         * content and not bound by the enclosing parent's bounding box.
         */
        .omni .dropdown-content {
          overflow-x: hidden;
          overflow-y: auto;
          position: fixed;
          padding: 0;
        }
        #dropdown-menu {
          padding-top: 1px;
          z-index: 40; /* set above omni-ui's app-layout header, same as modals */
        }

        /* Make divs looks similar to native inputs (borders, padding, etc) */
        :host,
        .dropdown-content {
          border-radius: 10px;
          color: rgb(var(--rgb-core-dark));
        }
        .dropdown-content {
          border: 1px solid rgb(219, 219, 219);
          background-color: rgb(var(--rgb-core-light));
        }
        .dropdown {
          border-radius: 10px;
        }
        .dropdown.is-active {
          background-color: rgb(var(--rgb-core-light));
        }
        :host {
          background-color: rgb(var(--rgb-core-medium-two));
          border: 1px solid rgb(var(--rgb-border));
        }
        :host([autowidth]) .dropdown-trigger,
        :host([autowidth]) {
          display: inline-block;
          min-width: 80px;
          width: fit-content;
        }
        :host([borderless]) {
          border: none;
        }
        #dropdown-trigger-container {
          padding: calc(0.42857 * var(--hem)) calc(0.67857 * var(--hem)); /* Same as native input elements */
          margin-bottom: -2px; /* Offset margin-bottom of child div/span elements */
          min-height: 35px;
        }

        /* Style the individual items in the dropdown list */
        .is-single .dropdown-trigger {
          user-select: none;
        }
        .omni .dropdown-item.is-search {
          overflow: unset;
          padding: 7px 6px 1px;
          position: sticky;
          top: 0;
          z-index: 1;
          background-color: rgb(var(--rgb-core-light));
          border-bottom: rgb(var(--rgb-border)) solid 1px;
          box-shadow: none !important;
        }
        .dropdown-item.is-search input {
          border: none;
          box-shadow: none;
          font-size: 14px;
          padding-left: 26px;
          background-color: rgb(var(--rgb-core-light));
        }

        .options-list {
          margin: 8px 0;
        }

        /* Tag/Trigger Text tweaks */
        .is-single #dropdown-trigger-container div {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        #dropdown-trigger-container .tag {
          background-color: rgb(var(--rgb-border));
          vertical-align: top; /* Without this there's an extra 1.5 pixels in height? */
        }
        #dropdown-trigger-container span.tag {
          font-size: calc(0.86 * var(--hem));
        }
        #dropdown-trigger-container .placeholder {
          background-color: unset;
          color: rgb(var(--rgb-core-dark));
          opacity: 0.3;
          border: none;
          padding: 0;
          font-size: var(--hem);
        }
        #dropdown-trigger-container span,
        #dropdown-trigger-container div {
          margin-bottom: 2px;
          height: calc(1.5 * var(--hem));
        }
        .is-single .dropdown-trigger span button {
          display: none;
        }

        /* Icon tweaks */
        .dropdown omni-icon {
          cursor: pointer;
          pointer-events: all;
        }
        #dropdown-trigger-container > omni-icon:last-of-type {
          margin-left: calc(0.571 * var(--hem));
        }
        .dropdown-trigger omni-icon {
          font-size: calc(1.29 * var(--hem));
          margin-top: calc(0.0714 * var(--hem));
        }
        .is-multi
          .dropdown-trigger
          omni-icon[icon-id='omni:interactive:remove'] {
          font-size: calc(1 * var(--hem));
        }

        #loader-span {
          display: inline-block;
          height: 20px;
          width: 20px;
          margin: 10px;
        }

        /* Similar to how input fields are styled, set width to 100% */
        div.dropdown,
        div.dropdown-trigger {
          width: 100%;
        }

        /* Styling of "focused" elements, particularly important if controlling
         * this dropdown with a keyboard. Last takes priority over hover etc.
         */
        *:focus {
          outline: none;
        }
        :host([borderless]) .dropdown {
          border-width: 0 !important;
        }
        .dropdown:focus,
        .dropdown:focus-visible {
          outline: none;
          border-color: rgb(var(--rgb-primary));
          border-radius: 10px;
          box-shadow: rgb(var(--rgb-primary)) 0px 0px 0px 1px !important;
        }
      `,
    ];
  }

  static register(name = 'om2-dropdown') {
    OmniElement.register(`${name}-impl`, Om2Dropdown);
    return OmniElement.register(name, formComponent(Om2Dropdown));
  }

  shouldUpdate(changed) {
    if (changed.has('search')) {
      if (this.search.length > 0) {
        this.getSearchResults();
      } else {
        this.resetSearch();
      }
    }
    return super.shouldUpdate(changed);
  }

  updated(changed) {
    if (changed.has('isActive') && changed.get('isActive') !== undefined) {
      const name = `dropdown-${this.isActive ? 'open' : 'close'}`;
      this.dispatchNewEvent(name);
    }
  }

  async getSearchResults() {
    if (this.asyncSearch) {
      this.lastSearchId = (this.lastSearchId || 0) + 1;
      const id = this.lastSearchId;
      const searchString = this.search;

      this.loading = true;
      await sleep(1000);
      if (id === this.lastSearchId) {
        // Still latest search
        const result = await this.asyncSearch(searchString);
        if (id === this.lastSearchId) {
          // Still latest search
          this.loading = false;
          this.filteredOptions = result.map(item => {
            let itemObj = {};
            if (typeof item === 'object') {
              itemObj = item;
            } else {
              itemObj[this.nameProperty] = item;
              itemObj[this.idProperty] = item;
            }
            return itemObj;
          });
        }
      }
      return;
    }

    // Normal search
    this.filteredOptions = this.options.filter(opt =>
      opt[this.nameProperty].toLowerCase().includes(this.search.toLowerCase())
    );
  }

  async pollAndFixPosition() {
    // Only while the dropdown is active, detect positioning issues and fix it
    const trigger = this.shadowRoot.querySelector(
      'div.dropdown .dropdown-trigger'
    );
    const content = this.shadowRoot.querySelector(
      'div.dropdown .dropdown-content'
    );

    // Keep hidden until properly positioned (avoid flicker/jump on open).
    // Only after second pass is it correctly positioned
    let firstPass = true;

    Object.assign(content.style, { visibility: 'hidden' });
    // Wait for dropdown to render itself
    await sleep(0);

    while (this.isActive) {
      const trigRect = trigger.getBoundingClientRect();
      let top = trigRect.bottom + 1;
      let maxHeight = Math.min(273, window.innerHeight - trigRect.bottom - 1);

      if (
        top + content.offsetHeight > window.innerHeight &&
        trigRect.top - 1 > window.innerHeight - top
      ) {
        // Not enough space to show below & there's more room above: "drop-up"
        top = trigRect.top - 1 - content.offsetHeight;
        maxHeight = Math.min(273, trigRect.top - 1);
      }

      Object.assign(content.style, {
        top: `${top}px`,
        left: `${trigRect.left}px`,
        ...(this.autoWidth ? {} : { width: `${trigRect.width}px` }),
        maxHeight: `${maxHeight}px`,
        visibility: firstPass ? 'hidden' : 'unset',
      });

      // eslint-disable-next-line no-await-in-loop
      await sleep(firstPass ? 0 : 200);
      firstPass = false;
    }
  }

  async focusOut() {
    // Need to wait (sleep) for activeElement to be set
    await sleep(0);
    if (!this.shadowRoot.activeElement) {
      // Focus shifted outside of dropdown, we're no longer active
      this.setActive(false);
    }
  }

  async focusIn(e) {
    const target = e.target || e.srcElement;
    const ignore = target.classList.contains('ignore-focus');

    if (!this.isActive && !ignore) {
      this.setActive(true);
    }
    if (this.isActive) {
      target.addEventListener('focusout', () => this.focusOut(), {
        once: true,
      });
    }
  }

  async setActive(to) {
    if (to === true) {
      this.resetSearch();
      this.isActive = true;
      this.pollAndFixPosition();
      if (this.hasSearch) {
        // wait for menu to become visible
        await sleep(0);
        await sleep(0);
        this.shadowRoot.querySelector('#input-search').focus();
      }
    } else {
      this.shadowRoot.querySelector('.dropdown-content').scrollTop = 0; // Scroll to top before hiding
      this.isActive = false;
    }
  }

  resetSearch() {
    if (this.hasSearch)
      this.shadowRoot.querySelector('div.is-search input').value = '';
    this.search = '';
    this.loading = false;
    this.lastSearchId += 1;
    this.filteredOptions = this.options;
  }

  toggleOptionComplete() {
    if (!this.isMulti) this.toggleActive();
  }

  toggleActive() {
    if (this.isActive) {
      this.shadowRoot.activeElement.blur();
      this.isActive = false;
    } else {
      this.shadowRoot.querySelector('.dropdown').focus();
    }
  }

  renderSearchInput() {
    if (!this.hasSearch) return '';
    return html`
      <div class="dropdown-item is-search">
        <p class="control has-icons-right">
          <input
            id="input-search"
            class="input"
            type="text"
            placeholder="Search"
            @keyup=${e => {
              this.search = e.target.value;
            }}
          />
          <span class="icon is-small is-right">
            ${(this.search || '').length === 0
              ? html`<omni-icon
                  class="is-size-1"
                  icon-id="omni:interactive:search"
                ></omni-icon>`
              : html`<omni-icon
                  class="is-size-1"
                  icon-id="omni:interactive:close"
                  @click=${this.resetSearch}
                ></omni-icon>`}
          </span>
        </p>
      </div>
    `;
  }

  renderContent() {
    if (this.loading) {
      return html` <div class="has-text-centered">
        <span id="loader-span" class="loader"></span>
      </div>`;
    }
    return this.filteredOptions.length === 0 && this.hasSearch
      ? ''
      : html`<div class="options-list">
          ${this.renderOptionsList(this.filteredOptions)}
        </div>`;
  }

  render() {
    const emAsPx = parseFloat(getComputedStyle(this.parentElement).fontSize);
    return html`
      <omni-style
        style="--hem: ${emAsPx}px"
        class="${this.isMulti ? 'is-multi' : 'is-single'} ${this.borderless
          ? 'borderless'
          : ''}"
      >
        <div
          tabindex="0"
          class="dropdown ${this.isActive ? 'is-active' : ''}"
          @focusin=${this.focusIn}
          @keyup=${e => {
            if (e.key === 'Escape') this.toggleActive();
          }}
        >
          <div class="dropdown-trigger" part="dropdown-trigger">
            <div
              id="dropdown-trigger-container"
              aria-haspopup="true"
              aria-controls="dropdown-menu"
            >
              <omni-icon
                part="dropdown-trigger-up-down-icon"
                class="ignore-focus is-pulled-right"
                icon-id="omni:interactive:${!this.isActive ? 'down' : 'up'}"
                @click=${this.toggleActive}
                tabindex="-1"
              ></omni-icon>

              ${this.allowClear && this.selected.length > 0
                ? html`
                    <omni-icon
                      class="ignore-focus is-pulled-right"
                      icon-id="omni:interactive:close"
                      @click=${() => {
                        this.selected = [];
                      }}
                      tabindex="-1"
                    ></omni-icon>
                  `
                : ''}
              ${this.renderSelected
                ? this.renderSelected(this, this.selected)
                : this.selected.map(
                    (option, i) => html`
                      ${this.isMulti
                        ? html` <span class="tag">
                            ${noblank(option[this.nameProperty])}
                            <omni-icon
                              class="ignore-focus ml-2"
                              icon-id="omni:interactive:remove"
                              @click=${e => this.removeOption(e, option, i)}
                              tabindex="-1"
                            ></omni-icon>
                          </span>`
                        : html`<div title="${option[this.nameProperty]}">
                            ${noblank(option[this.nameProperty])}
                          </div>`}
                    `
                  )}
              ${this.selected.length === 0 && !this.renderSelected
                ? html`<div
                    part="dropdown-trigger-placeholder"
                    class="${this.isMulti ? 'tag' : ''} placeholder"
                  >
                    ${noblank(this.renderPlaceholder(this.placeholder))}
                  </div>`
                : ''}
            </div>
          </div>

          <div
            class="dropdown-menu"
            id="dropdown-menu"
            part="dropdown-menu"
            role="menu"
          >
            <div class="dropdown-content">
              ${this.renderSearchInput()} ${this.renderContent()}
            </div>
          </div>
        </div>
      </omni-style>
    `;
  }
}

Om2Dropdown.register();
